import { type KeybindingConfig, SHORTCUTS, type ShortcutId } from '@/shared/config/shortcuts';
import { type ShortcutHandler, useShortcutStore } from '@/shared/stores/shortcutStore';

/**
 * The shortcut registry API — generic and feature-free so it lives in `shared/`.
 * It mirrors the dialog registry's imperative façade but follows VS Code's
 * command/keybinding split: the *keys* live in the central `SHORTCUTS` config,
 * and features register only a *handler* for a command id. Valid ids are
 * type-checked against the config ({@link ShortcutId}) — there is no
 * declaration-merged interface to augment; add a shortcut by adding a config
 * entry.
 *
 * `registerShortcut` resolves the command's keybinding from config and stores an
 * active binding in {@link useShortcutStore}, which the app-root
 * `<ShortcutHost/>` reads to dispatch. App-wide commands register at bootstrap
 * (`src/app/shortcutRegistry.ts`); feature-owned commands register on mount via
 * `useRegisterShortcut` and are torn down on unmount (mount-scoped — the key
 * divergence from the register-and-keep dialog registry).
 */

export type { ShortcutHandler };

/** Bind a handler to a centrally-configured command. Resolves its keybinding from `SHORTCUTS`. */
export function registerShortcut(id: ShortcutId, handler: ShortcutHandler): void {
  const config: KeybindingConfig = SHORTCUTS[id];
  useShortcutStore.getState().register(id, {
    keys: config.keys,
    scope: config.scope ?? 'global',
    handler,
    preventDefault: config.preventDefault,
    allowInInput: config.allowInInput,
  });
}

/** Remove a previously registered command handler. */
export function unregisterShortcut(id: ShortcutId): void {
  useShortcutStore.getState().unregister(id);
}
